import { GridEditInputCell } from '@mui/x-data-grid';
import { IconButton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';

import theme from '@theme';

import { convertUnits, convertEntityQuantityToInpulseUnit } from '@commons/utils/conversion';
import { formatNumber } from '@commons/DisplayNumber';
import { handleKeyDown } from '@commons/utils/dataGrid';
import { isBetweenDays, isSameOrAfterDay, isSameOrBeforeDay } from '@commons/utils/date';
import Text, { ENUM_FONTS, ENUM_COLORS } from '@commons/Text';

import { COLUMN_TYPES } from './constants';

import {
  NameCellContainer,
  RoundPicture,
  Name,
  NameHover,
  Sku,
  RowContainer,
  RecipeBatchProductionRowsContainer,
  HeaderCellContainer,
} from './styledComponents';

const DEFAULT_PRODUCT_PICTURE = '/images/inpulse/product-photo-placeholder.svg';
const DEFAULT_RECIPE_PICTURE = '/images/inpulse/recipe-photo-placeholder.svg';
const DEFAULT_INGREDIENT_PICTURE = '/images/inpulse/ingredient-photo-placeholder.svg';
const MAXIMUM_NAME_LENGTH = 22;

const DEFAULT_IMAGE = {
  [COLUMN_TYPES.PRODUCTS]: DEFAULT_PRODUCT_PICTURE,
  [COLUMN_TYPES.RECIPES]: DEFAULT_RECIPE_PICTURE,
  [COLUMN_TYPES.INGREDIENTS]: DEFAULT_INGREDIENT_PICTURE,
};

const commonCellStyle = {
  width: '60px',
  height: '40px',
  justifyContent: 'center',
  border: 1,
  borderRadius: 2,
  fontSize: 14,
};

export const isCellEditable = (launchDate, endDate, field) => {
  if (!launchDate && !endDate) {
    return true;
  }

  const currentDate = moment(field);

  if (!launchDate && endDate) {
    return isSameOrBeforeDay(currentDate, endDate);
  }

  if (!endDate && launchDate) {
    return isSameOrAfterDay(currentDate, launchDate);
  }

  return isBetweenDays(currentDate, launchDate, endDate);
};

export const renderNameCell = (name, type, { sku = null, withImg = false, imgSrc = null } = {}) => {
  const [displayBigger, setDisplayBigger] = useState(false);

  const img = imgSrc || DEFAULT_IMAGE[type];

  return (
    <NameCellContainer>
      {withImg && <RoundPicture src={img} />}
      <Stack>
        <Name onMouseOut={() => setDisplayBigger(false)} onMouseOver={() => setDisplayBigger(true)}>
          {name}
        </Name>
        {displayBigger && name && name.length > MAXIMUM_NAME_LENGTH && (
          <NameHover offsetName onMouseOut={() => setDisplayBigger(false)}>
            {name}
          </NameHover>
        )}
        {sku && <Sku>{sku}</Sku>}
      </Stack>
    </NameCellContainer>
  );
};

export const renderCategoryCell = (name) => {
  const [displayBigger, setDisplayBigger] = useState(false);

  const formattedCategoryName = name || i18next.t('GENERAL.NONE_VALUE');

  return (
    <NameCellContainer>
      <Name onMouseOut={() => setDisplayBigger(false)} onMouseOver={() => setDisplayBigger(true)}>
        {formattedCategoryName}
      </Name>
      {displayBigger && name && name.length > MAXIMUM_NAME_LENGTH && (
        <NameHover offsetCategory onMouseOut={() => setDisplayBigger(false)}>
          {formattedCategoryName}
        </NameHover>
      )}
    </NameCellContainer>
  );
};

export const renderStandardHeader = (props) => {
  const { colDef } = props;

  return (
    <NameCellContainer>
      <Text font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>{colDef.headerName}</Text>
    </NameCellContainer>
  );
};

export const renderInputHeader = (props, onZoomClick, showZoom = false) => {
  const { colDef } = props;

  return (
    <NameCellContainer>
      <HeaderCellContainer>
        <Text font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>{colDef.headerName}</Text>
        {colDef.headerSubTitle && (
          <Text font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>{colDef.headerSubTitle}</Text>
        )}
      </HeaderCellContainer>
      {showZoom && onZoomClick && (
        <IconButton
          onClick={() => {
            onZoomClick(colDef);
          }}
        >
          <img src={'/images/inpulse/zoom.svg'} />
        </IconButton>
      )}
    </NameCellContainer>
  );
};

/**
 *  Used for an input cell of products table
 * */
export const renderInputCell = (props) => {
  const {
    isEditable,
    value,
    field,
    row: { adminProductForecastDates, launchDate, endDate },
  } = props;

  const hasAdminPF = adminProductForecastDates?.includes(field);

  const formattedValue = value !== null && value !== undefined ? Math.round(value) : '-';

  return (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      sx={{
        ...commonCellStyle,
        cursor: isEditable && 'pointer',
        borderColor:
          hasAdminPF && isEditable ? theme.colors.greys.darkest : theme.colors.greys.lighty,
        backgroundColor: !isEditable && theme.colors.greys.lighty,
      }}
    >
      <Text font={hasAdminPF ? ENUM_FONTS.TEXT_MIDDLE_INTER_BOLD : ENUM_FONTS.TEXT_MIDDLE_NORMAL}>
        {isCellEditable(launchDate, endDate, field) ? formattedValue : '-'}
      </Text>
    </Stack>
  );
};

// We have to do this because the sx props doesn't work in this case
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    border: `1px solid ${theme.colors.infoRed}`,
    backgroundColor: theme.colors.greys.lightest,
  },
}));

export const renderEditInputCell = (props) => {
  const { error, row, field, hasFocus } = props;
  const { adminProductForecastDates } = row;

  const hasAdminPF = adminProductForecastDates?.includes(field);

  return (
    <StyledTooltip
      {...props}
      open={!!error}
      title={
        <Text color={ENUM_COLORS.INFO_RED}>
          {i18next.t('FORECAST.POS_PRODUCTION_PLANNING.CELL_INPUT_ERROR')}
        </Text>
      }
    >
      <GridEditInputCell
        {...props}
        sx={{
          ...commonCellStyle,
          color: error ? theme.colors.infoRed : 'inherit',
          borderColor:
            hasAdminPF || hasFocus ? theme.colors.greys.darkest : theme.colors.greys.lighty,
          textAlign: 'center',
          '& .MuiInputBase-input': {
            textAlign: 'center',
          },
        }}
        onFocus={(event) => event.target.select()}
        onKeyDown={handleKeyDown}
      />
    </StyledTooltip>
  );
};

const TotalStack = ({ children }) => (
  <Stack
    alignItems="center"
    direction="row"
    justifyContent="space-between"
    sx={{ width: '100%', height: '100%' }}
  >
    {children}
  </Stack>
);

const refreshIcon = (onRefreshClick, { row }) => (
  <IconButton
    onClick={() => {
      onRefreshClick({ row });
    }}
  >
    <img src={'/images/inpulse/refresh-black-small.svg'} />
  </IconButton>
);

export const renderTotalCell = (props, type, { onRefreshClick = {} } = {}) => {
  const { value, row } = props;
  const { isViewDayMode, hasDynamicForecast, selectedDate, hasAdminPF } = row;

  // convertUnits will always return undefined for products
  const convertedUnit = convertUnits(row.unit, true) || i18next.t('GENERAL.MINIFIED_UNIT');

  const font = ENUM_FONTS.TEXT_SMALL_HEIGHT_16;

  if (value === null) {
    return (
      <TotalStack>
        <Text font={font}>{`- ${convertedUnit}`}</Text>
      </TotalStack>
    );
  }

  if (value === 0) {
    // When stricly 0, ignore decimals, ignore batch production
    return (
      <TotalStack>
        <Text font={font}>{`0 ${convertedUnit}`}</Text>
      </TotalStack>
    );
  }

  const convertedTotal = convertEntityQuantityToInpulseUnit(value, row.unit);
  const formattedTotal =
    type === COLUMN_TYPES.PRODUCTS ? Math.round(convertedTotal) : formatNumber(convertedTotal, 2);

  if (row.batchProduction) {
    const convertedQuantity = formatNumber(
      convertEntityQuantityToInpulseUnit(
        !isViewDayMode ? row.quantity : row.unitQuantity,
        row.unit,
      ),
      2,
    );

    return (
      <RecipeBatchProductionRowsContainer isTotalCell>
        <Text font={font}>{`${formattedTotal} ${convertedUnit}`}</Text>
        <Text color={ENUM_COLORS.IP_BLACK_5} font={font}>{`(${formatNumber(
          convertedTotal / convertedQuantity,
          2,
        )} x ${convertedQuantity} ${convertedUnit})`}</Text>
      </RecipeBatchProductionRowsContainer>
    );
  }

  const isInPast = moment(selectedDate).isBefore(moment(), 'day');

  const displayRefreshIcon = isViewDayMode && hasDynamicForecast && hasAdminPF && !isInPast;

  return (
    <TotalStack>
      <Text font={font}>{`${formattedTotal} ${convertedUnit}`}</Text>
      {displayRefreshIcon && refreshIcon(onRefreshClick, { row })}
    </TotalStack>
  );
};

export const renderRecipeQuantityCell = ({ value, row }) => {
  const convertedUnit = convertUnits(row.unit, true, value);
  const convertedValue = convertEntityQuantityToInpulseUnit(value, row.unit);

  const formattedValue =
    value != null && !isNaN(convertedValue) ? formatNumber(convertedValue, 2) : '-';

  if (!row.batchProduction) {
    return (
      <RowContainer>
        <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>{`${formattedValue} ${convertedUnit}`}</Text>
      </RowContainer>
    );
  }

  const convertedQuantity = formatNumber(
    convertEntityQuantityToInpulseUnit(
      !row.isViewDayMode ? row.quantity : row.unitQuantity,
      row.unit,
    ),
    2,
  );

  return (
    <RecipeBatchProductionRowsContainer>
      <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>{`${formattedValue} ${convertedUnit}`}</Text>
      {value != null && !!convertedQuantity && (
        <Text
          color={ENUM_COLORS.IP_BLACK_5}
          font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}
        >{`${formatNumber(
          convertedValue / convertedQuantity,
          2,
        )} x ${convertedQuantity} ${convertedUnit}`}</Text>
      )}
    </RecipeBatchProductionRowsContainer>
  );
};

export const renderIngredientQuantityCell = ({ value, row }) => {
  const convertedValue = convertEntityQuantityToInpulseUnit(value, row.unit);
  const convertedUnit = convertUnits(row.unit, true, value);

  const formattedValue =
    value != null && !isNaN(convertedValue) ? formatNumber(convertedValue, 2) : '-';

  return (
    <RowContainer>
      <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>{`${formattedValue} ${convertedUnit}`}</Text>
    </RowContainer>
  );
};

/** With time slots */

export const renderInputCellWithTimeSlots = (props) => {
  const {
    isEditable,
    value,
    row: { hasAdminPF, launchDate, endDate, selectedDate },
  } = props;

  const formattedValue = value ? Math.round(value) : value;

  return (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      sx={{
        ...commonCellStyle,
        cursor: isEditable && 'pointer',
        borderColor:
          hasAdminPF && isEditable ? theme.colors.greys.darkest : theme.colors.greys.lighty,
        backgroundColor: !isEditable && theme.colors.greys.lighty,
      }}
    >
      <Text font={hasAdminPF ? ENUM_FONTS.TEXT_MIDDLE_INTER_BOLD : ENUM_FONTS.TEXT_MIDDLE_NORMAL}>
        {isCellEditable(launchDate, endDate, selectedDate) ? formattedValue : '-'}
      </Text>
    </Stack>
  );
};

export const renderEditInputCellWithTimeSlots = (props) => {
  const { error, row, hasFocus } = props;
  const { hasAdminPF } = row;

  return (
    <StyledTooltip
      {...props}
      open={!!error}
      title={
        <Text color={ENUM_COLORS.INFO_RED}>
          {i18next.t('FORECAST.POS_PRODUCTION_PLANNING.CELL_INPUT_ERROR')}
        </Text>
      }
    >
      <GridEditInputCell
        {...props}
        sx={{
          ...commonCellStyle,
          color: error ? theme.colors.infoRed : 'inherit',
          borderColor:
            hasAdminPF || hasFocus ? theme.colors.greys.darkest : theme.colors.greys.lighty,
          textAlign: 'center',
          '& .MuiInputBase-input': {
            textAlign: 'center',
          },
        }}
        onFocus={(event) => event.target.select()}
        onKeyDown={handleKeyDown}
      />
    </StyledTooltip>
  );
};
